import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ClientResultCard = ({ 
  client, 
  searchQuery, 
  onAddMinutes, 
  onSubtractMinutes 
}) => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  const highlightSearchTerm = (text, query) => {
    if (!query || !text) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-accent/30 text-accent-foreground px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  const getBalanceStatus = (balance) => {
    if (balance === 0) return { color: 'text-muted-foreground', status: 'Empty' };
    if (balance < 30) return { color: 'text-warning', status: 'Low' };
    if (balance < 60) return { color: 'text-accent', status: 'Medium' };
    return { color: 'text-success', status: 'Good' };
  };

  const handleCardClick = () => {
    navigate(`/client-detail/${client.id}`);
  };

  const handleQuickAction = async (action, amount) => {
    setIsProcessing(true);
    try {
      if (action === 'add') {
        await onAddMinutes(client.id, amount);
      } else {
        await onSubtractMinutes(client.id, amount);
      }
    } catch (error) {
      console.error('Quick action error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'No phone number';
    return phone.replace(/\D/g, '');
  };

  const balanceInfo = getBalanceStatus(client.balance);
  const minutes = client.balance || 0;
  const sessions = client.sessions || 0;

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
      {/* Card Header - Clickable */}
      <div 
        className="p-6 cursor-pointer hover:bg-muted/30 transition-colors duration-150"
        onClick={handleCardClick}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {highlightSearchTerm(client.name, searchQuery)}
            </h3>
            <p className="text-muted-foreground text-sm">
              {highlightSearchTerm(formatPhoneNumber(client.phone), searchQuery)}
            </p>
          </div>
          <div className="text-right space-y-1">
            {minutes > 0 && (
              <div>
                <div className={`text-2xl font-bold font-mono ${balanceInfo.color}`}>
                  {minutes}
                </div>
                <div className="text-xs text-muted-foreground">
                  Total Minutes
                </div>
              </div>
            )}

            {sessions > 0 && (
              <div>
                <div className="text-lg font-semibold font-mono text-foreground">
                  {sessions}
                </div>
                <div className="text-xs text-muted-foreground">
                  Sessions
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Balance Status Indicator */}
        <div className="flex items-center space-x-2 mb-4">
          <div className={`w-2 h-2 rounded-full ${
            client.balance === 0 ? 'bg-muted-foreground' :
            client.balance < 30 ? 'bg-warning' :
            client.balance < 60 ? 'bg-accent' : 'bg-success'
          }`}></div>
          <span className={`text-xs font-medium ${balanceInfo.color}`}>
            {balanceInfo.status} Balance
          </span>
        </div>

        {/* Last Activity */}
        <div className="flex items-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Clock" size={12} />
          <span>Last visit: {client.lastVisit}</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-6 py-4 bg-muted/20 border-t border-border">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Quick Actions
          </span>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleQuickAction('add', 30);
              }}
              loading={isProcessing}
              iconName="Plus"
              iconPosition="left"
              iconSize={14}
              className="text-xs hover:bg-success hover:text-success-foreground hover:border-success transition-all duration-150"
            >
              +30min
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleQuickAction('subtract', 15);
              }}
              loading={isProcessing}
              disabled={client.balance < 15}
              iconName="Minus"
              iconPosition="left"
              iconSize={14}
              className="text-xs hover:bg-warning hover:text-warning-foreground hover:border-warning transition-all duration-150 disabled:opacity-50"
            >
              -15min
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientResultCard;